// frontend/src/utils/conversation/buildTurnInterpretation.js

import { getIntentInfo } from '../intents/intentPreprocessor';
import {
  shouldBypassIntentFallback,
  buildAttachedTurnIntent,
} from './shouldBypassIntentFallback';

import { debugLog } from '../logging/debugLog';

function normalizeText(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function countWords(value = '') {
  const normalized = normalizeText(value);

  if (!normalized) return 0;

  return normalized.split(/\s+/).filter(Boolean).length;
}

function normalizeIntentCandidate(intentInfo = null) {
  if (!intentInfo || typeof intentInfo !== 'object') {
    return {
      intent: 'unknown',
      target: null,
      memoryName: null,
      source: 'none',
      confidence: 0,
      vectorHints: [],
    };
  }

  const normalizedIntent =
    intentInfo.intent && intentInfo.intent !== 'null'
      ? intentInfo.intent
      : 'unknown';

  return {
    ...intentInfo,
    intent: normalizedIntent,
    target: intentInfo.target || null,
    memoryName: intentInfo.memoryName || null,
    source: intentInfo.source || 'classifier',
    confidence:
      typeof intentInfo.confidence === 'number'
        ? intentInfo.confidence
        : normalizedIntent === 'unknown'
          ? 0.25
          : 0.65,
    vectorHints: Array.isArray(intentInfo.vectorHints)
      ? intentInfo.vectorHints
      : [],
  };
}

function getAttachedEntity({
  conversationState = null,
  fallbackEntity = null,
} = {}) {
  const activeRequestTarget =
    conversationState?.activeFrameState?.situation?.turnOperations
      ?.activeRequestTarget || null;

  if (activeRequestTarget) {
    return activeRequestTarget;
  }

  const relationshipReferenceState =
    conversationState?.relationshipReferenceState || null;

  if (
    relationshipReferenceState?.resolved === true &&
    relationshipReferenceState?.primaryEntity
  ) {
    return relationshipReferenceState.primaryEntity;
  }

  const pronounResolutionSignal =
    conversationState?.pronounResolutionSignal || null;

  if (
    pronounResolutionSignal?.resolved === true &&
    pronounResolutionSignal?.candidateEntity
  ) {
    return pronounResolutionSignal.candidateEntity;
  }

  return (
    conversationState?.entityState?.active ||
    conversationState?.reference?.lastReferencedEntity ||
    conversationState?.turnAttachment?.resolvedEntity ||
    fallbackEntity ||
    null
  );
}

function buildRelationshipReferenceIntent(relationshipReferenceState = null) {
  if (
    relationshipReferenceState?.shouldControlIntent !== true ||
    !relationshipReferenceState?.targetRole
  ) {
    return null;
  }

  return {
    intent: 'recall_request',
    target: relationshipReferenceState.targetRole,
    targetType: 'role',
    memoryName: relationshipReferenceState.primaryEntity?.name || null,
    source: 'relationship_reference_state',
    confidence: relationshipReferenceState.confidence || 0,
    inheritedEntity: relationshipReferenceState.primaryEntity || null,
    relationshipReferenceState,
    vectorHints: [],
  };
}

function getAttachedTopic({
  conversationState = null,
  fallbackTopic = null,
} = {}) {
  return (
    conversationState?.reference?.lastReferencedTopic ||
    conversationState?.turnAttachment?.resolvedTopic ||
    fallbackTopic ||
    null
  );
}

function getInterpretationSource({
  explicitRelationshipDeclaration = null,
  fallbackBypassed = false,
  turnIntentCandidate = null,
} = {}) {
  if (explicitRelationshipDeclaration) {
    return 'relationship_declaration';
  }

  if (fallbackBypassed) {
    return turnIntentCandidate?.source || 'turn_attachment';
  }

  return turnIntentCandidate?.source || 'classifier';
}

async function buildTurnInterpretation({
  message = '',
  conversationState = null,
  lastTarget = null,
  relationships = [],
  fallbackEntity = null,
  fallbackTopic = null,
  explicitRelationshipDeclaration = null,
} = {}) {
  const normalizedMessage = normalizeText(message);
  const wordCount = countWords(message);

  const arbitration =
    conversationState?.turnSignalArbitration || null;

  const pronounResolutionSignal =
    conversationState?.pronounResolutionSignal || null;

  const attachedEntity = getAttachedEntity({
    conversationState,
    fallbackEntity,
  });

  const attachedTopic = getAttachedTopic({
    conversationState,
    fallbackTopic,
  });

  const relationshipReferenceState =
    conversationState?.relationshipReferenceState || null;

  const turnOperations =
    conversationState?.activeFrameState?.situation?.turnOperations || null;

  const relationshipReferenceIntent =
    buildRelationshipReferenceIntent(relationshipReferenceState);

  let rawIntentCandidate = null;
  let fallbackUsed = false;
  let fallbackBypassed = false;

  if (explicitRelationshipDeclaration) {
    rawIntentCandidate = {
      ...explicitRelationshipDeclaration,
      source:
        explicitRelationshipDeclaration.source ||
        'relationship_declaration',
      confidence:
        typeof explicitRelationshipDeclaration.confidence === 'number'
          ? explicitRelationshipDeclaration.confidence
          : 0.98,
    };
  } else if (relationshipReferenceIntent) {
    rawIntentCandidate = relationshipReferenceIntent;
    fallbackBypassed = true;
  } else {
    fallbackBypassed = shouldBypassIntentFallback({
      message,
      turnAttachment: conversationState?.turnAttachment || null,
      lastReferencedEntity: attachedEntity,
      turnSignalArbitration: arbitration,
      pronounResolutionSignal,
    });

    if (fallbackBypassed) {
      rawIntentCandidate = buildAttachedTurnIntent({
        lastReferencedEntity: attachedEntity,
        lastReferencedTopic: attachedTopic,
        pronounResolutionSignal,
      });
    } else {
      fallbackUsed = true;

      rawIntentCandidate = await getIntentInfo(
        message,
        lastTarget,
        relationships
      );
    }
  }

  const turnIntentCandidate =
    normalizeIntentCandidate(rawIntentCandidate);

  const source = getInterpretationSource({
    explicitRelationshipDeclaration,
    fallbackBypassed,
    turnIntentCandidate,
  });

  const interpretation = {
    message: String(message || '').trim(),
    normalizedMessage,
    wordCount,

    turnIntentCandidate,

    attachedEntity,
    attachedTopic,

    explicitRelationshipDeclaration:
      explicitRelationshipDeclaration || null,

    relationshipReferenceState,
    turnOperations,

    fallbackUsed,
    fallbackBypassed,

    source,

    continuity: {
      attached:
        conversationState?.turnAttachment?.attached === true,

      anchorType:
        conversationState?.turnAttachment?.anchorType || 'none',

      turnAct:
        conversationState?.turnAttachment?.turnAct || 'unknown',

      attachmentConfidence:
        conversationState?.turnAttachment?.confidence || 0,

      pronounResolved:
        pronounResolutionSignal?.resolved === true,

      pronounScore:
        pronounResolutionSignal?.score || 0,

      relationshipResolved:
        relationshipReferenceState?.resolved === true,

      relationshipConfidence:
        relationshipReferenceState?.confidence || 0,

      relationshipTarget:
        relationshipReferenceState?.primaryEntity?.name || null,

      pendingAnswerTarget:
        turnOperations?.pendingAnswerTarget?.name || null,

      activeRequestTarget:
        turnOperations?.activeRequestTarget?.name || null,

      compoundTurn:
        turnOperations?.compound === true,

      dominantSignal:
        arbitration?.dominantSignal || 'none',

      dominantSignalScore:
        arbitration?.dominantSignalScore || 0,
    },
  };

  debugLog(
    '🧭 [TurnInterpretation]',
    interpretation
  );

  return interpretation;
}

export default buildTurnInterpretation;